import { Modal, Form, Input, Select, InputNumber, Row, Col, Typography, Grid, Space, Button, Switch } from "antd";
import { PlusOutlined, DeleteOutlined, SaveOutlined, CloseOutlined, BankOutlined, InfoCircleOutlined, PercentageOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import type { ProvidersData, ProviderPayload } from "../../helpers/interfaces/ProvidersInterfaces";
import { useFetchProviderTypes } from "../../hooks/providers/useFetchProviderTypes";
import { useFetchInstallmentRatesCFGroups } from "../../hooks/providers/useFetchInstallmentRatesCFGroups";

const { Text } = Typography;
const { Option } = Select;

type ProviderModalComponentProps = {
  open: boolean;
  record: ProvidersData | null;
  onCancel: () => void;
  onSave: (values: any) => void;
  loading?: boolean;
};


export const ProviderModalComponent = ({
  open,
  record,
  onCancel,
  onSave,
  loading = false,
}: ProviderModalComponentProps) => {
  const [form] = Form.useForm();
  const screens = Grid.useBreakpoint();
  const isUpdate = !!record;
  const { providerTypes, loading: typesLoading } = useFetchProviderTypes();
  const { installmentGroups, loading: groupsLoading } = useFetchInstallmentRatesCFGroups();

  useEffect(() => {
    if (open) {
      if (record) {
        console.log(record, 'record')
        // Assume API returns it as decimal. 
        // If percentage mode is active, display (value * 100)
        const formValues = {
          provider_name: record.provider_name,
          provider_type_id: record.provider_type_id,
          status: record.is_active === 1 ? "LIVE" : "INACTIVE", 
          monthly_terms: record.monthly_terms?.map((term: any) => ({
            installment_cf_group_id: term.installment_cf_group_id,
            interest_value: Number(((term.interest_value ?? term.interest_rate ?? 0) * 100).toFixed(6)),
          })) || [],
        };
        form.resetFields();
        form.setFieldsValue(formValues);
      } else {
        form.resetFields();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, open, form]);

  // Watch terms to handle disabling already selected ones
  const monthlyTerms = Form.useWatch('monthly_terms', form) || [];
  const selectedTermIds = monthlyTerms.map((t: any) => t?.installment_cf_group_id).filter(Boolean);


  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      
      const rawTerms = values.monthly_terms || [];
      const apiReadyRates = rawTerms.map((term: any) => ({
        installment_cf_group_id: term.installment_cf_group_id,
        interest_value: Number((term.interest_value / 100).toFixed(4)),
      }));

      // Detect if rates were modified
      const originalTerms = record?.monthly_terms || [];
      const isRatesChanged = 
        originalTerms.length !== apiReadyRates.length || 
        originalTerms.some((orig, i) => 
          orig.installment_cf_group_id !== apiReadyRates[i].installment_cf_group_id || 
          Number(orig.interest_value).toFixed(4) !== apiReadyRates[i].interest_value.toFixed(4)
        );

      // Map to the specific structure strictly typed by the interface
      const payload: ProviderPayload = {
        provider_name: values.provider_name,
        provider_type_id: values.provider_type_id,
        is_active: values.status === "LIVE" ? 1 : 0,
      };

      if (!isUpdate || isRatesChanged) {
        payload.monthly_terms = apiReadyRates;
      }

      console.log(payload, 'payload')

      onSave(payload);
    } catch {
      // validation errors handled by antd
    }
  };

  const modalWidth = 720;

  const customHeader = (
    <div style={{
      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
      padding: '24px',
      margin: '-20px -24px 24px -24px',
      borderRadius: '8px 8px 0 0',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    }}>
      <div style={{ 
        background: 'rgba(255,255,255,0.15)', 
        padding: '12px', 
        borderRadius: '12px', 
        display: 'flex',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(4px)'
      }}>
        <BankOutlined style={{ fontSize: '28px', color: '#fff' }} />
      </div>
      <div>
        <div style={{ fontSize: '20px', fontWeight: 600, letterSpacing: '-0.3px' }}>
          {isUpdate ? "Update Provider" : "Create New Provider"}
        </div>
        <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '2px' }}>
          Fill in the provider details and configure monthly interest terms
        </div>
      </div>
    </div>
  );

  const cardStyle = {
    background: '#ffffff',
    border: '1px solid #d6dee8',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.03)'
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: '20px'
  };

  return (
    <Modal
      title={customHeader}
      closable={false}
      open={open}
      onOk={handleOk}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      okText={isUpdate ? "Save Changes" : "Create Provider"}
      cancelText="Cancel"
      confirmLoading={loading}
      centered
      width={screens.xs ? "100%" : modalWidth}
      style={screens.xs ? { top: 0, padding: 0 } : { paddingBottom: 0 }}
      bodyStyle={{ overflowY: "auto", overflowX: "hidden", maxHeight: screens.xs ? "calc(100vh - 120px)" : "calc(100vh - 220px)" }}
      okButtonProps={{
        icon: <SaveOutlined />,
        size: 'large',
        style: { backgroundColor: "#1e293b", borderColor: "#1e293b", borderRadius: "8px", fontWeight: 500, padding: '0 24px' },
      }}
      cancelButtonProps={{
        icon: <CloseOutlined />,
        size: 'large',
        style: { borderRadius: "8px", fontWeight: 500, padding: '0 24px' }
      }}
    >
      {isUpdate && (
        <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #334155' }}>
          <Text type="secondary" style={{ fontSize: '13px' }}>
            Editing Provider ID: <Text strong style={{ color: '#1e293b' }}>#{record?.provider_id}</Text>
          </Text>
        </div>
      )}

      <Form form={form} layout="vertical" size="large">
        <div style={cardStyle}>
          <div style={headerStyle}>
            <InfoCircleOutlined style={{ color: '#334155' }} />
            General Information
          </div>
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={24}>
              <Form.Item 
                name="provider_name" 
                label={<span style={{ fontWeight: 500 }}>Provider Name</span>} 
                rules={[{ required: true, message: "Please enter provider name" }]}
              >
                <Input placeholder="e.g. TFS, BDO, etc." style={{ borderRadius: '6px' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item 
                name="provider_type_id" 
                label={<span style={{ fontWeight: 500 }}>Provider Type</span>} 
                rules={[{ required: true, message: "Please select provider type" }]}
              >
                <Select placeholder="Select Type" loading={typesLoading}>
                  {providerTypes.map((type) => (
                    <Option key={type.provider_type_id} value={type.provider_type_id}>
                      {type.provider_type_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item 
                name="status" 
                label={<span style={{ fontWeight: 500 }}>Status</span>} 
                rules={[{ required: true }]} 
                initialValue="LIVE"
              >
                <Select>
                  <Option value="LIVE">
                    <span style={{ color: '#22c55e', fontWeight: 500 }}>● LIVE</span>
                  </Option>
                  <Option value="INACTIVE">
                    <span style={{ color: '#333333ff', fontWeight: 500 }}>● INACTIVE</span>
                  </Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div style={{ ...headerStyle, marginBottom: 0 }}>
              <PercentageOutlined style={{ color: '#334155' }} />
              Monthly Interest Rates
            </div>
          </div>
          
          <div>
            <Form.List name="monthly_terms">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ 
                      marginBottom: 16, 
                      padding: "16px", 
                      background: "#fbfcfd", 
                      borderRadius: "8px", 
                      border: "1px solid #d6dee8",
                      position: 'relative',
                      transition: 'all 0.3s'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: screens.xs ? 'column' : 'row',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        gap: '16px',
                        width: '100%'
                      }}>
                        <div style={{ flex: 1, width: '100%' }}>
                          <Form.Item
                            {...restField}
                            name={[name, 'installment_cf_group_id']}
                            label={<span style={{ fontSize: '13px', fontWeight: 500, color: '#475569' }}>Term Duration</span>}
                            rules={[{ required: true, message: 'Missing duration' }]}
                            style={{ marginBottom: screens.xs ? '16px' : 0 }}
                          >
                            <Select placeholder="Select Term" loading={groupsLoading}>
                              {installmentGroups.map(opt => (
                                <Option 
                                  key={opt.installment_rates_cf_group_id} 
                                  value={opt.installment_rates_cf_group_id}
                                  disabled={selectedTermIds.includes(opt.installment_rates_cf_group_id) && form.getFieldValue(['monthly_terms', name, 'installment_cf_group_id']) !== opt.installment_rates_cf_group_id}
                                >
                                  {opt.installment_rates_cf_group_label}
                                </Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </div>

                        <div style={{ flex: 1, width: '100%' }}>
                          <Form.Item
                            noStyle
                            shouldUpdate={(prev, curr) => prev.monthly_terms !== curr.monthly_terms}
                          >
                            {() => {
                              const value = form.getFieldValue(['monthly_terms', name, 'interest_value']);
                              const decimal = value ? (value / 100).toFixed(4) : '0.000';
                              return (
                                <Form.Item
                                  {...restField}
                                  name={[name, 'interest_value']}
                                  label={
                                    <span style={{ fontSize: '13px', fontWeight: 500, color: '#475569' }}>
                                      Interest Rate (%) <span style={{ color: '#64748b', marginLeft: '8px' }}>| Decimal: {decimal}</span>
                                    </span>
                                  }
                                  rules={[{ required: true, message: 'Missing rate' }]}
                                  style={{ marginBottom: screens.xs ? '16px' : 0 }}
                                >
                                  <InputNumber 
                                    style={{ width: "100%", borderRadius: '6px' }} 
                                    min={0} 
                                    step={0.01} 
                                    placeholder="e.g. 35.4"
                                  />
                                </Form.Item>
                              );
                            }}
                          </Form.Item>
                        </div>

                        <div style={{ width: screens.xs ? '100%' : '48px' }}>
                          <Form.Item label={screens.xs ? null : " "} style={{ marginBottom: 0 }}>
                            <Button 
                              type="text" 
                              size="large"
                              danger 
                              icon={<DeleteOutlined />} 
                              onClick={() => remove(name)}
                              style={{ 
                                width: '100%', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                background: '#fff0f6', 
                                borderColor: '#ffa39e',
                                borderRadius: '8px'
                              }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  ))}
                  <Form.Item style={{ marginTop: fields.length > 0 ? 16 : 0, marginBottom: 0 }}>
                    <Button 
                      type="dashed" 
                      onClick={() => add()} 
                      block 
                      icon={<PlusOutlined />}
                      style={{ 
                        height: '48px', 
                        borderColor: '#d6dee8', 
                        color: '#334155', 
                        borderRadius: '8px',
                        fontWeight: 500,
                        background: '#fbfcfd'
                      }}
                    >
                      Add Interest Rate Entry
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>
        </div>
      </Form>
    </Modal>
  );
};
